import { type QueryKey, useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	type ApiResponse,
	type ProviderOperationsAction,
	ProviderOperationsExecutionStatus,
	type ProviderOperationsSearchResponse,
	type ProviderOperationsSearchRowResponse,
	toApiError,
} from "libs/apiClient";
import client from "libs/client";
import { useCallback, useRef, useState } from "react";
import { useToast } from "ui-kit";
import { ProviderOperationsBulkRowOutcome } from "./getProviderOperationsBulkSummary";
import { getProviderOperationsErrorMessage } from "./getProviderOperationsErrorMessage";
import { isGameScopedAction } from "./isGameScopedAction";
import {
	PROVIDER_OPERATIONS_BULK_ACTION_MUTATION_KEY,
	PROVIDER_OPERATIONS_ROW_ACTION_MUTATION_KEY,
} from "./providerOperationsMutationKeys";
import { replaceProviderOperationsRows } from "./replaceProviderOperationsRows";

/** The action endpoint answers 400 when the action is no longer available and 404 when the request is gone. */
const NO_LONGER_ELIGIBLE_STATUSES = [400, 404];

export type ProviderOperationsBulkProgress = {
	action: ProviderOperationsAction;
	completedCount: number;
	totalCount: number;
	isStopping: boolean;
};

type BulkActionVariables = {
	action: ProviderOperationsAction;
	rows: ProviderOperationsSearchRowResponse[];
};

/**
 * Rows run one request at a time, so each request stays within the gateway timeout and its result shows as soon as it finishes.
 */
export function useProviderOperationsBulkAction(searchQueryKey: QueryKey) {
	const queryClient = useQueryClient();
	const { addErrorToast } = useToast();
	const [progress, setProgress] = useState<ProviderOperationsBulkProgress>();
	const isStopRequestedRef = useRef(false);
	const rowActionMutation = client.postProviderOperationsAction.useMutation();

	const runRow = async (
		row: ProviderOperationsSearchRowResponse,
		action: ProviderOperationsAction,
	): Promise<ProviderOperationsBulkRowOutcome> => {
		try {
			const response = await rowActionMutation.mutateAsync({ id: row.id, body: { provider: row.provider, action } });
			if (response === undefined) {
				return ProviderOperationsBulkRowOutcome.FAILED;
			}

			queryClient.setQueryData<ApiResponse<ProviderOperationsSearchResponse>>(searchQueryKey, (searchResult) =>
				replaceProviderOperationsRows(searchResult, [response.data]),
			);

			return response.data.latestExecution?.status === ProviderOperationsExecutionStatus.enum.success
				? ProviderOperationsBulkRowOutcome.SUCCEEDED
				: ProviderOperationsBulkRowOutcome.FAILED;
		} catch (error) {
			const apiError = toApiError(error);
			if (NO_LONGER_ELIGIBLE_STATUSES.includes(apiError.status)) {
				return ProviderOperationsBulkRowOutcome.SKIPPED;
			}

			addErrorToast(getProviderOperationsErrorMessage(apiError.json));

			return ProviderOperationsBulkRowOutcome.FAILED;
		}
	};

	const runRows = async ({ action, rows }: BulkActionVariables): Promise<ProviderOperationsBulkRowOutcome[]> => {
		const rowOutcomes: ProviderOperationsBulkRowOutcome[] = [];
		isStopRequestedRef.current = false;
		setProgress({ action, completedCount: 0, totalCount: rows.length, isStopping: false });

		for (const row of rows) {
			rowOutcomes.push(await runRow(row, action));
			if (isStopRequestedRef.current) {
				break;
			}

			setProgress({ action, completedCount: rowOutcomes.length, totalCount: rows.length, isStopping: false });
		}

		setProgress(undefined);
		if (isGameScopedAction(action)) {
			await queryClient.invalidateQueries(searchQueryKey);
		}

		return rowOutcomes;
	};

	const bulkActionMutation = useMutation({
		mutationKey: PROVIDER_OPERATIONS_BULK_ACTION_MUTATION_KEY,
		mutationFn: runRows,
	});

	const runBulkAction = (action: ProviderOperationsAction, rows: ProviderOperationsSearchRowResponse[]) =>
		bulkActionMutation.mutateAsync({ action, rows });

	const stopBulkAction = useCallback(() => {
		isStopRequestedRef.current = true;
		setProgress((currentProgress) => (currentProgress === undefined ? undefined : { ...currentProgress, isStopping: true }));
	}, []);

	return { progress, runBulkAction, stopBulkAction };
}

export type ProviderOperationsBulkAction = ReturnType<typeof useProviderOperationsBulkAction>;

export function useIsBulkActionRunning(): boolean {
	return useIsMutating({ mutationKey: PROVIDER_OPERATIONS_BULK_ACTION_MUTATION_KEY }) > 0;
}

export function useIsAnyRowActionRunning(): boolean {
	return useIsMutating({ mutationKey: PROVIDER_OPERATIONS_ROW_ACTION_MUTATION_KEY }) > 0;
}
